import React from 'react'
import tw from 'twin.macro'
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import { RowActionsMenu } from 'src/components/shared/grid/RowActionsMenu'
import {
  transactionPaymentTypeLabels,
  transactionReferenceTypeLabels,
} from 'src/constants/payments-ops/labels'
import type {
  TransactionPaymentType,
  TransactionReferenceType,
} from 'src/constants/payments-ops/enums'
import type { MenuActionItem } from 'src/interfaces/ui'
import type { PaymentsOpsTransaction } from 'src/interfaces/transactionsReview'
import { formatDateTime, parseUnixTimestamp } from 'src/utils/grid'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'
import { resolvePartnerName } from 'src/utils/paymentsOps/partnerNames'
import { pickTransactionDetails } from 'src/utils/paymentsOps/transactionDetails'

interface TransactionColumnOptions {
  variant: 'incoming' | 'outgoing'
  nameMap: Record<string, string>
  onViewDetails(transaction: PaymentsOpsTransaction): void
  /** Incoming: a menu of actions. Absent = no permitted action; the actions column is dropped. */
  buildActions?(transaction: PaymentsOpsTransaction): MenuActionItem[]
  /** Outgoing: one primary action. Absent = no `transactions_authorize`; the column is dropped. */
  onAuthorize?(transaction: PaymentsOpsTransaction): void
  isSubmitting: boolean
}

const monoCell = (params: GridRenderCellParams<PaymentsOpsTransaction, string | undefined>) => {
  return <span css={tw`truncate font-mono text-xs`}>{params.value ?? '—'}</span>
}

/** Every column `sortable: false` — both endpoints order server-side (`createdAt DESC`). */
export const buildTransactionColumns = ({
  variant,
  nameMap,
  onViewDetails,
  buildActions,
  onAuthorize,
  isSubmitting,
}: TransactionColumnOptions): Array<GridColDef<PaymentsOpsTransaction>> => {
  const columns: Array<GridColDef<PaymentsOpsTransaction>> = [
    {
      field: 'createdAt',
      headerName: 'Created at',
      width: 180,
      sortable: false,
      valueGetter: parseUnixTimestamp,
      valueFormatter: formatDateTime,
    },
    {
      field: 'partnerName',
      headerName: 'Partner',
      flex: 1,
      minWidth: 160,
      sortable: false,
      valueGetter: (value: string | null | undefined, row: PaymentsOpsTransaction) => {
        return resolvePartnerName(nameMap, row.partnerId, value)
      },
    },
    {
      field: 'id',
      headerName: 'Transaction ID',
      width: 150,
      sortable: false,
      renderCell: monoCell,
    },
    {
      field: 'externalTransactionId',
      headerName: 'External ID',
      width: 160,
      sortable: false,
      renderCell: monoCell,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      valueFormatter: (value: number) => {
        return formatCentsAsUsd(value)
      },
    },
  ]

  if (variant === 'outgoing') {
    columns.push({
      field: 'effectiveDate',
      headerName: 'Effective date',
      width: 140,
      sortable: false,
      valueGetter: (value: string | undefined) => {
        return value ?? '—'
      },
    })
  }

  columns.push(
    { field: 'type', headerName: 'Type', width: 100, sortable: false },
    {
      field: 'paymentType',
      headerName: 'Payment type',
      width: 130,
      sortable: false,
      valueGetter: (value: TransactionPaymentType | undefined) => {
        if (!value) {
          return '—'
        }

        return transactionPaymentTypeLabels[value] ?? value
      },
    },
    {
      field: 'referenceType',
      headerName: 'Reference type',
      width: 170,
      sortable: false,
      valueGetter: (value: TransactionReferenceType | undefined) => {
        if (!value) {
          return '—'
        }

        return transactionReferenceTypeLabels[value] ?? value
      },
    },
    {
      field: 'details',
      headerName: 'Details',
      width: 90,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<PaymentsOpsTransaction>) => {
        if (!pickTransactionDetails(params.row)) {
          return <span>—</span>
        }

        return (
          <Button
            size="small"
            color="white"
            iconOnly
            aria-label="View transaction details"
            leftIcon={<GSIcon iconName="gs_eye_open" size={20} css={tw`text-shades-1000`} />}
            onClick={() => {
              onViewDetails(params.row)
            }}
            data-testid={`transaction-details-${params.row.id}`}
          />
        )
      },
    },
  )

  // Removed, not left empty, when nothing may be done to a row (spec §7): outgoing without
  // `transactions_authorize`, incoming without any permitted menu action.
  const hasRowActions =
    variant === 'outgoing' ? onAuthorize !== undefined : buildActions !== undefined

  if (!hasRowActions) {
    return columns
  }

  columns.push({
    field: 'actions',
    headerName: '',
    // "Authorize…" is wider than "Execute…", which clipped at 120 in the refunds grid.
    width: variant === 'outgoing' ? 160 : 70,
    sortable: false,
    display: 'flex',
    renderCell: (params: GridRenderCellParams<PaymentsOpsTransaction>) => {
      if (variant === 'outgoing') {
        return (
          <Button
            size="small"
            color="white"
            disabled={isSubmitting}
            onClick={() => {
              onAuthorize?.(params.row)
            }}
            data-testid={`outgoing-authorize-${params.row.id}`}
          >
            Authorize…
          </Button>
        )
      }

      return (
        <RowActionsMenu
          items={buildActions ? buildActions(params.row) : []}
          ariaLabel="Transaction actions"
          dataTestId={`incoming-actions-${params.row.id}`}
        />
      )
    },
  })

  return columns
}
